import { Trans, useLingui } from "@lingui/react/macro";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Spinner,
  toast,
} from "@rakazo/ui-web";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@rakazo/ui-web/components/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { errorText } from "../../lib/user-error";
import { drokRpc } from "../rpc";
import { AssignmentsPanel } from "./assignments-panel";
import { PublishDialog } from "./publish-dialog";
import { VersionView } from "./version-view";

/** One template: its versions, publish and archive, and who has it. */
export function AdminTemplateDetailPage() {
  const { t } = useLingui();
  const { templateId = "" } = useParams();
  const queryClient = useQueryClient();
  const [viewing, setViewing] = useState<number | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["drok", "admin.template", templateId],
    queryFn: () => drokRpc.templates.get({ templateId }),
  });
  const archive = useMutation({
    mutationFn: () => drokRpc.templates.archive({ templateId }),
    onSuccess: async () => {
      toast.success(t`Template archived`);
      setArchiving(false);
      await queryClient.invalidateQueries({ queryKey: ["drok"] });
    },
    onError: (e) => setArchiveError(errorText(e)),
  });
  const data = query.data;
  return (
    <section className="space-y-6" data-testid="admin-template-detail">
      <NavLink
        to="/admin/templates"
        className="text-sm text-muted-foreground hover:text-foreground"
      >
        <Trans>All templates</Trans>
      </NavLink>
      {query.isPending ? (
        <div className="grid place-items-center p-8">
          <Spinner aria-label={t`Loading…`} />
        </div>
      ) : query.isError ? (
        <div role="alert" className="rounded-xl bg-muted/50 p-4 text-sm">
          <p className="text-destructive">{errorText(query.error)}</p>
          <Button className="mt-2" size="sm" variant="outline" onClick={() => void query.refetch()}>
            <Trans>Try again</Trans>
          </Button>
        </div>
      ) : data ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">
              {data.template.name}
              {data.template.archivedAt ? (
                <Badge variant="outline" className="ms-2">
                  <Trans>Archived</Trans>
                </Badge>
              ) : null}
            </h2>
            {data.template.archivedAt ? null : (
              <div className="flex gap-2">
                <Button onClick={() => setPublishing(true)}>
                  <Trans>Publish a new version</Trans>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setArchiveError(null);
                    setArchiving(true);
                  }}
                >
                  <Trans>Archive</Trans>
                </Button>
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Trans>Version</Trans>
                  </TableHead>
                  <TableHead>
                    <Trans>Notes</Trans>
                  </TableHead>
                  <TableHead>
                    <Trans>Published</Trans>
                  </TableHead>
                  <TableHead className="text-end" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.versions.map((version) => (
                  <TableRow key={version.id} data-testid="template-version-row">
                    <TableCell>v{version.version}</TableCell>
                    <TableCell>{version.notes}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {new Date(version.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setViewing(version.version)}
                      >
                        <Trans>View</Trans>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <AssignmentsPanel
            templateId={templateId}
            archived={data.template.archivedAt !== null}
            versions={data.versions.map((v) => ({ id: v.id, version: v.version }))}
          />
          <PublishDialog
            target={
              publishing
                ? {
                    kind: "version",
                    templateId,
                    name: data.template.name,
                    sourceBotId: data.template.sourceBotId,
                  }
                : null
            }
            onClose={() => setPublishing(false)}
            onPublished={() => undefined}
          />
          <AlertDialog
            open={archiving}
            onOpenChange={(open) => (open || archive.isPending ? undefined : setArchiving(false))}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  <Trans>Archive {data.template.name}?</Trans>
                </AlertDialogTitle>
                <AlertDialogDescription>
                  <Trans>It can no longer be assigned or get new versions.</Trans>
                </AlertDialogDescription>
              </AlertDialogHeader>
              {archiveError ? (
                <p role="alert" className="text-sm text-destructive" data-testid="archive-error">
                  {archiveError}
                </p>
              ) : null}
              <AlertDialogFooter>
                <AlertDialogCancel disabled={archive.isPending}>
                  <Trans>Cancel</Trans>
                </AlertDialogCancel>
                <Button
                  variant="destructive"
                  disabled={archive.isPending}
                  onClick={() => {
                    setArchiveError(null);
                    archive.mutate();
                  }}
                >
                  {archive.isPending ? <Spinner aria-label={t`Working…`} /> : null}
                  <Trans>Archive</Trans>
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : null}
      <VersionView templateId={templateId} version={viewing} onClose={() => setViewing(null)} />
    </section>
  );
}
